import axios from "axios";
import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useLoadingState } from "../Context";
import OrderTimeline from "./OrderTimeLine";
import { Box, Grid, Paper, Step, StepConnector, StepLabel, Stepper, Typography, stepConnectorClasses } from "@mui/material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import OrderDetails from "./OrderDetail";
import OrderNotFoundScreen from "./OrderNotFoundScreen";
import Action from "./Action";
import { domainDetails } from "../domainDetails";
import { useAppContext } from "../AppContext";
import useIsMobile from "./isMobile";
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MarkunreadMailboxOutlinedIcon from '@mui/icons-material/MarkunreadMailboxOutlined';

const stepperSteps = [
    { label: 'Packed', shortLabel: 'Packed', icon: 1 },
    { label: 'In Transit', shortLabel: 'Transit', icon: 2 },
    { label: 'Out for Delivery', shortLabel: 'Out', icon: 3 },
    { label: 'Delivered', shortLabel: 'Done', icon: 4 },
];

const stepIcons = {
    1: <InventoryOutlinedIcon sx={{ fontSize: 18 }} />,
    2: <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />,
    3: <MarkunreadMailboxOutlinedIcon sx={{ fontSize: 18 }} />,
    4: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />,
};

function getStepperIndex(bucket) {
    const mapping = {
        'Shipment Packed': 0,
        'Shipment In Transit': 1,
        'Out for delivery': 2,
        'Out for Delivery': 2,
        'Delivered': 3,
    };
    return mapping[bucket] ?? -1;
}

function getStatusColors(textColor, palette) {
    const colorMap = {
        primary: { main: palette.primary.main, light: alpha(palette.primary.main, 0.08), gradient: `linear-gradient(135deg, ${palette.primary.main}, ${palette.primary.light})` },
        success: { main: palette.success.main, light: alpha(palette.success.main, 0.08), gradient: `linear-gradient(135deg, ${palette.success.main}, ${palette.success.light})` },
        error: { main: palette.error.main, light: alpha(palette.error.main, 0.08), gradient: `linear-gradient(135deg, ${palette.error.main}, ${palette.error.light})` },
    };
    return colorMap[textColor] || colorMap.primary;
}

const ColorConnector = styled(StepConnector)(({ theme, statuscolor }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 16,
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            background: statuscolor || theme.palette.primary.main,
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            background: statuscolor || theme.palette.primary.main,
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 2,
        border: 0,
        backgroundColor: '#e0e0e0',
        borderRadius: 1,
    },
}));

function ColorStepIcon({ active, completed, icon, statuscolor }) {
    const theme = useTheme();
    const colors = getStatusColors(statuscolor, theme.palette);
    return (
        <Box
            sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...(completed || active
                    ? {
                        background: colors.gradient,
                        color: '#fff',
                        boxShadow: `0 2px 6px ${colors.main}30`,
                    }
                    : {
                        bgcolor: '#fafafa',
                        color: '#bdbdbd',
                        border: '1.5px solid #e0e0e0',
                    }),
                transition: 'all 0.3s ease',
            }}
        >
            {stepIcons[icon]}
        </Box>
    );
}

export default function Order() {
    const [searchParams] = useSearchParams()
    const { id } = useParams()
    const { isLoading, setIsLoading } = useLoadingState()
    const [data, setData] = React.useState([])
    const [found, setFound] = React.useState(true)

    const theme = useTheme();
    const { setValues } = useAppContext();
    const isMobile = useIsMobile();

    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    let host = window.location.host
    const subdomain = host?.split('.')[0]
    let dd = domainDetails[subdomain];
    let merchantId = dd?.id;

    let passedQasQueryParam = searchParams.get('q');
    let searchBy = searchParams.get('searchBy');
    let value = searchParams.get('ref_awb_number');
    let awbSearch = searchParams.get('awbNo');

    if (passedQasQueryParam) {
        searchBy = 'waybill';
        value = passedQasQueryParam;
    }

    const fetchData = (request) => {
        setIsLoading(true);

        let req = axios.post(`https://r1fiocxmcf.execute-api.ap-south-1.amazonaws.com/proship/track_waybill_p`, {
            queryStringParameters: request
        });

        req.then((response) => {

            const orderDetails = response.data?.message?.waybillDetails?.pop();
            let orderFound = true;

            if ((orderDetails.failedReason &&  ['wrong awb or awb doesn\'t exist', 'wrong shipping or shipping doesn\'t exist'].includes(orderDetails.failedReason))) {
                orderFound = false;
            }

            if (!orderFound) {
                setValues({ askForVerification: false, orderDetails: null });
            } else {
                const failedDelivery = orderDetails.currentStatus === 'FAILED_DELIVERY';
                if (failedDelivery) {
                    setValues({ askForVerification: true, orderDetails: orderDetails });
                } else {
                    setValues({ askForVerification: false, orderDetails: orderDetails });
                }
            }

            setData(orderDetails || []);
            setFound(orderFound);

        }).catch((err) => {
            console.error(err);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    React.useEffect(() => {
        const request = {}
        if ((searchBy && value) || id || awbSearch) {
            setIsLoading(true);
            if (searchBy === 'reference') {
                request.reference = value;
            } else if (searchBy === 'waybill') {
                request.waybills = value;
            } else if (id) {
                request.waybills = id;
            } else if (awbSearch) {
                request.waybills = awbSearch;
            }

            if (merchantId) {
                request.merchantId = merchantId;
            }

            fetchData(request);
        }
        // eslint-disable-next-line
    }, [searchBy, value, id, awbSearch]);


    let textColor = 'primary';
    let edd;
    if (data.createdDate) {
        if (data?.edd) {
            edd = new Date(data?.edd);
        }
        if (data.currentStatus.includes('DELIVERED')) {
            textColor = 'success'
        } else if (data.currentStatus.includes('FAILED')) {
            textColor = 'error'
        }
    }

    const statusBucket = data.createdDate ? mapStatusToBucket(data.currentStatus).replace(/_/g, " ") : '';
    const activeStepIndex = getStepperIndex(statusBucket);
    const showStepper = activeStepIndex >= 0;
    const hasCustomColors = !!dd?.colors;
    const effectiveTextColor = hasCustomColors ? 'primary' : textColor;
    const statusColors = getStatusColors(effectiveTextColor, theme.palette);
    const showEdd = edd && ['Shipment Packed', 'Shipment In Transit', 'Out for delivery', 'Out for Delivery', 'Delivery Failed'].includes(statusBucket);

    return (
        <Grid container spacing={2}>
            {data.createdDate && (
                <>
                    {showStepper && (
                        <Grid item xs={12}>
                            <Paper elevation={0} sx={{ px: { xs: 1.5, sm: 4 }, py: { xs: 1.5, sm: 2 } }}>
                                <Stepper
                                    activeStep={activeStepIndex}
                                    alternativeLabel
                                    connector={<ColorConnector statuscolor={statusColors.main} />}
                                >
                                    {stepperSteps.map((step, index) => (
                                        <Step key={step.label}>
                                            <StepLabel
                                                StepIconComponent={(props) => (
                                                    <ColorStepIcon {...props} statuscolor={effectiveTextColor} icon={step.icon} />
                                                )}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    fontWeight={index <= activeStepIndex ? 600 : 500}
                                                    color={index <= activeStepIndex ? 'text.primary' : 'text.secondary'}
                                                    sx={{
                                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{step.label}</Box>
                                                    <Box sx={{ display: { xs: 'block', sm: 'none' } }}>{step.shortLabel}</Box>
                                                </Typography>
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Paper>
                        </Grid>
                    )}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', order: { xs: 2, md: 1 } }}>
                        <Paper
                            elevation={0}
                            sx={{
                                width: '100%',
                                overflow: 'hidden',
                            }}>
                            <Box sx={{
                                px: { xs: 2.5, sm: 4 },
                                py: { xs: 2.5, sm: 4 },
                                background: `linear-gradient(135deg, ${statusColors.light} 0%, #ffffff 100%)`,
                                borderBottom: `3px solid ${statusColors.main}`,
                            }}>
                                <Box flexDirection={{
                                    xs: 'column',
                                    sm: 'row'
                                }}
                                    alignItems={{
                                        xs: 'flex-start',
                                        sm: 'center',
                                    }}
                                    sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: `${effectiveTextColor}.main`,
                                            boxShadow: `0 0 0 3px ${statusColors.main}30`,
                                        }} />
                                        <Typography color={`${effectiveTextColor}.main`} variant="h6" fontWeight={700}>
                                            {statusBucket}
                                        </Typography>
                                    </Box>
                                    <Action />
                                </Box>
                                {showEdd && (
                                    <Box sx={{ mt: 2, pl: 3.5 }}>
                                        <Typography
                                            variant="overline"
                                            fontWeight={600}
                                            component="div"
                                            color="text.secondary"
                                            sx={{ letterSpacing: 1.2, lineHeight: 1.5 }}
                                        >
                                            {statusBucket === 'Delivery Failed' ? 'Original EDD' : 'Expected Delivery'}
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                                            {days[edd.getDay()]}, {months[edd.getMonth()]} {edd.getDate()}, {edd.getFullYear()}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{
                                p: { xs: 2.5, sm: 4 },
                                pt: { xs: 2, sm: 3 },
                                maxHeight: { xs: 'none', md: 480 },
                                overflowY: { xs: 'auto', md: 'hidden' },
                                ...(!isMobile ? {
                                    '&:hover': {
                                        overflowY: 'scroll !important',
                                    },
                                } : {}),
                                '&::-webkit-scrollbar': {
                                    width: '4px'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#d0d0d0',
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'transparent'
                                }
                            }}>
                                <OrderTimeline orderDetailInfo={data} resolvedStatusColor={statusColors.main} />
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <OrderDetails data={data} statusBucket={statusBucket} statusColor={effectiveTextColor} />
                            </Grid>
                        </Grid>
                    </Grid>
                </>
            )}
            {!isLoading && (!found) && (
                <Grid item xs={12}>
                    <OrderNotFoundScreen />
                </Grid>
            )}
        </Grid>
    )
}

function mapStatusToBucket(status) {
    const statusMapping = {
        'ORDER_PLACED': 'Shipment Packed',
        'PICKUP_PENDING': 'Shipment Packed',
        'PICKUP_FAILED': 'Shipment Packed',
        'PICKED_UP': 'Shipment In Transit',
        'INTRANSIT': 'Shipment In Transit',
        'OUT_FOR_DELIVERY': 'Out for delivery',
        'DELIVERED': 'Delivered',
        'FAILED_DELIVERY': 'Delivery Failed',
        'CANCELLED_ORDER': 'Cancelled',
        'RTO_REQUESTED': 'Shipment Returned',
        'RTO': 'Shipment Returned',
        'RTO_OUT_FOR_DELIVERY': 'Shipment Returned',
        'RTO_DELIVERED': 'Shipment Returned',
        'RTO_FAILED': 'Shipment Returned',
        'LOST': 'Lost',
        'DAMAGED': 'Lost',
        'SHIPMENT_DELAYED': 'Shipment In Transit',
        'CONTACT_CUSTOMER_CARE': 'Shipment In Transit',
        'SHIPMENT_HELD': 'Shipment In Transit',
        'RTO_INTRANSIT': 'Shipment Returned',
        'OUT_FOR_PICKUP': 'Shipment Packed',
        'RTO_CONTACT_CUSTOMER_CARE': 'Shipment Returned',
        'RTO_SHIPMENT_DELAY': 'Shipment Returned',
        'AWB_REGISTERED': 'Shipment Packed',
        'EXCHANGE_PICKUP': 'Shipment In Transit',
        'EXCHANGE_INTRANSIT': 'Shipment In Transit',
        'EXCHANGE_DELIVERED': 'Delivered',
        'MANIFESTED': 'Shipment Packed',
        'RETURN ORDER PLACED': 'Shipment In Transit'
    };

    return statusMapping[status] || status;
}
